import * as Yup from "yup";

import { Link, Redirect, useLocation } from "react-router-dom";

import AuthLayout, {
  AuthProviderButton,
  Divider,
  FormHeader,
  authProviders,
} from "components/auth/AuthLayout";
import Button from "components/global/Button";
import CheckBox from "components/global/CheckBox";
import FormGroup from "components/global/FormGroup";
import { Formik } from "formik";
import Spacer from "components/global/Spacer";
import { connect } from "react-redux";
import { loginUser } from "api";
import { extractEmails, showAlert } from "utils/utils";
import styled from "styled-components";
import { useEffect } from "react";
import Logo from "components/global/Logo";
import usePageTitle from "hooks/usePageTitle";

const FormWrapper = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 2.4rem;

  .spanFull {
    grid-column: 1/3;
  }
`;

const Login = ({ logged_in, user_accounts }) => {
  usePageTitle("Sign in");
  const { search } = useLocation();
  const email = extractEmails(search)?.[0];
  const add_account = new URLSearchParams(search).get("add_account");
  const redirectPath = new URLSearchParams(search).get("return_to");
  const successMessage = new URLSearchParams(search).get("success");
  const error = new URLSearchParams(search).get("error");

  const handleLogin = async (cred) => {
    const res = await loginUser(cred);

    if (res?.data?.status === false || res?.data?.errors) {
      showAlert({
        msg: res?.data?.message ?? "Incorrect email or password",
        type: "danger",
      });

      return;
    }
  };

  const schema = Yup.object({
    email: Yup.string()
      .email("Invalid email address")
      .required("Field required"),
    password: Yup.string().required("Field required"),
  });

  useEffect(() => {
    const rmCheck = document.getElementById("rememberMe");

    if (error && !logged_in) {
      showAlert({
        msg: error,
        type: "success",
      });
    }

    if (localStorage.checkbox && localStorage.checkbox !== "") {
      rmCheck?.click();
    } else {
      rmCheck?.removeAttribute("checked");
    }

    if (successMessage) {
      showAlert({
        type: "success",
        msg: successMessage,
      });
    }
  });

  if (!add_account && logged_in && user_accounts !== null)
    return (
      <Redirect
        to={
          !user_accounts?.length && !redirectPath?.includes("accept-invite")
            ? "/welcome"
            : redirectPath ?? "/dashboard/posts"
        }
      />
    );

  return (
    <AuthLayout>
      <FormHeader>
        <Logo />
        <p className="title-1">
          Don't have an account?{" "}
          <Link
            to={
              redirectPath ? `/sign-up${search}` : "/sign-up"
            }
            className="bold"
          >
            Sign up
          </Link>
        </p>
      </FormHeader>
      <Spacer y={6} yMobile={3.6} />
      <h3 className="title colorGrey1">Sign in to PostPaddy</h3>
      <Spacer y={0.6} />
      <p className="description">
        Manage and schedule posts on mutiple social media accounts.
      </p>
      <Spacer y={2.4} />
      {authProviders.map((provider) => (
        <AuthProviderButton key={provider.name} href={provider.target}>
          <img src={provider.icon} alt={provider.name} className="icon" />
          <span className="l2">{provider.buttonText}</span>
        </AuthProviderButton>
      ))}
      <Divider>
        <span className="l3 text">or</span>
      </Divider>
      <Spacer y={2.4} />
      <Formik
        initialValues={{
          email: (email || localStorage.username) ?? "",
          password: "",
        }}
        validationSchema={schema}
        onSubmit={async (values, { setSubmitting }) => {
          setSubmitting(true);
          await handleLogin(values);
          setSubmitting(false);
        }}
      >
        {({ handleSubmit, isSubmitting, isValid, values }) => (
          <FormWrapper onSubmit={handleSubmit}>
            <FormGroup
              fieldStyle="shortText"
              type="email"
              label="Email address"
              name="email"
              className="spanFull"
            />
            <FormGroup
              fieldStyle="shortText"
              type="password"
              label="Password"
              name="password"
              className="password spanFull"
            />
            <div className="flexRow justifySpaceBetween spanFull">
              <CheckBox
                id="rememberMe"
                name="rememberMe"
                label="Remember me"
                value="rememberMe"
              />
              <div>
                <Link
                  to="/password-reset"
                  className="colorSecondary forgotPassword"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              text="Sign in"
              className="spanFull"
              disabled={
                isSubmitting || !isValid || !values.email || !values.password
              }
              loading={isSubmitting}
              fullWidth
            />
          </FormWrapper>
        )}
      </Formik>
    </AuthLayout>
  );
};

const mapStateToProps = (state) => {
  return {
    logged_in: state.logged_in,
    user_accounts: state.user_accounts,
  };
};

export default connect(mapStateToProps)(Login);
